import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { AuthContextData, AuthProviderProps } from './types'
import { AuthCustomerUserArgs, CustomerModel, useCustomerGetMeQuery, useLoginCustomerUserMutation } from '~/graphql/types'
import { getToken, removeToken, storeSessionToken, storeToken } from '~/utils/token'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApolloError } from '@apollo/client'
import { toast } from 'react-toastify'
import { clearString } from '~/utils'
import { LoginFormData } from '~/pages/Login/components/LoginForm'

const AuthContext = createContext({} as AuthContextData)

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authUser, setAuthUser] = useState<null | CustomerModel>(null)
  const [getUserIsLoading, setGetUserIsLoading] = useState(true)
  const [loginUser, { loading: loginIsLoading }] = useLoginCustomerUserMutation({ fetchPolicy: 'no-cache' })
  const { refetch: refetchGetMe } = useCustomerGetMeQuery({ fetchPolicy: 'no-cache' })
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const authLogin = async (formData: LoginFormData) => {
    try {
      const result = await loginUser({
        variables: {
          params: {
            password: formData.password,
            username: clearString(formData.username)
          }
        }
      })

      if (result.data) {
        if(formData.rememberMe) {
          storeToken(result.data.loginCustomerUser.accessToken)
        } else {
          storeSessionToken(result.data.loginCustomerUser.accessToken)
        }

        await getMe()
      }
    } catch (err: any) {
      const apolloError = err as ApolloError
      const errMessage = apolloError.graphQLErrors[0]
      toast.error(errMessage?.extensions?.code === 'BAD_USER_INPUT' ? 'E-mail ou senha incorretos' : 'houve um problema ao realizar o login', { autoClose: 2000 })
    }
  }

  const getMe = useCallback(async () => {
    setGetUserIsLoading(true)
    try {
      const response = await refetchGetMe()

      if (response.data) {
        setAuthUser(response.data.customerGetMe as CustomerModel)
        if (!pathname.includes('/app/contracts/')) {
          navigate('/app/contracts')
        }
      } else {
        removeToken()
      }

    } catch (err) {
      removeToken()
    } finally {
      setGetUserIsLoading(false)
    }
  }, [navigate])

  const logout = useCallback(() => {
    removeToken()
    setAuthUser(null)
    navigate('/')
  }, [])

  useEffect(() => {
    const token = getToken()
    if (token) {
      getMe()
    }
    else {
      setGetUserIsLoading(false)
      if (pathname.includes('/app')) {
        navigate('/')
      }
    }
  }, [])

  return (
    <AuthContext.Provider value={{
      setAuthUser,
      authUser,
      getUserIsLoading,
      authLogin,
      loginIsLoading,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
